import { getAnalytics as firebaseGetAnalytics, isSupported as firebaseIsSupported, logEvent as firebaseLogEvent } from "firebase/analytics";

import { FIREBASE_CONFIG, getFirebaseApp } from "./firebase-backup.js";

const MATCH_EVENT_NAMES = Object.freeze({
  normal: {
    started: "game_started",
    finished: "game_finished"
  },
  revenge: {
    started: "revenge_started",
    finished: "revenge_finished"
  }
});

let configuredTestMode = false;
let analyticsInstancePromise = null;

export function configureFirebaseAnalytics({ testMode = false } = {}) {
  configuredTestMode = Boolean(testMode);
  if (configuredTestMode) {
    analyticsInstancePromise = null;
  }
}

export function initializeFirebaseAnalytics(dependencies = {}) {
  return runBestEffort(async () => {
    await getAnalyticsInstance(dependencies);
  });
}

export function trackMatchStarted(details = {}, dependencies = {}) {
  const mode = normalizeMode(details.mode);
  return trackAnalyticsEvent(
    MATCH_EVENT_NAMES[mode].started,
    createMatchPayload(details),
    dependencies
  );
}

export function trackMatchFinished(details = {}, dependencies = {}) {
  const mode = normalizeMode(details.mode);
  return trackAnalyticsEvent(
    MATCH_EVENT_NAMES[mode].finished,
    createMatchPayload(details, { includeResult: true, includeDuration: true }),
    dependencies
  );
}

export function trackAchievementUnlocked(details = {}, dependencies = {}) {
  const achievementId = normalizeText(details.achievementId);
  if (!achievementId) {
    return Promise.resolve();
  }

  return trackAnalyticsEvent(
    "achievement_unlocked",
    {
      achievement_id: achievementId,
      difficulty: normalizeDifficulty(details.difficulty),
      source: normalizeSource(details.source)
    },
    dependencies
  );
}

function trackAnalyticsEvent(eventName, params, dependencies = {}) {
  return runBestEffort(async () => {
    if (isAnalyticsTestMode(dependencies)) {
      return;
    }

    const analytics = await getAnalyticsInstance(dependencies);
    if (!analytics) {
      return;
    }

    const log = dependencies.logEvent ?? firebaseLogEvent;
    await log(analytics, eventName, params);
  });
}

async function getAnalyticsInstance(dependencies = {}) {
  if (dependencies.analytics === null || isAnalyticsTestMode(dependencies)) {
    return null;
  }
  if (dependencies.analytics) {
    return dependencies.analytics;
  }

  if (hasInjectedDependencies(dependencies)) {
    return createAnalyticsInstance(dependencies);
  }

  if (!analyticsInstancePromise) {
    analyticsInstancePromise = createAnalyticsInstance(dependencies).catch(() => null);
  }
  return analyticsInstancePromise;
}

async function createAnalyticsInstance(dependencies = {}) {
  const supported = await (dependencies.isSupported ?? firebaseIsSupported)();
  if (!supported) {
    return null;
  }

  const app = dependencies.app ?? getFirebaseApp(dependencies.config ?? FIREBASE_CONFIG);
  return (dependencies.getAnalytics ?? firebaseGetAnalytics)(app);
}

function createMatchPayload(details = {}, options = {}) {
  const signedIn = Boolean(details.signedIn);
  const payload = {
    difficulty: normalizeDifficulty(details.difficulty),
    signed_in: signedIn,
    auth_provider: normalizeAuthProvider(details.authProvider, signedIn)
  };

  if (options.includeResult) {
    payload.result = normalizeResult(details.result);
  }

  if (options.includeDuration) {
    const durationMs = normalizeDurationMs(details.durationMs);
    if (durationMs !== null) {
      payload.duration_ms = durationMs;
    }
  }

  return payload;
}

async function runBestEffort(action) {
  try {
    await action();
  } catch {
    // Analytics must never affect gameplay.
  }
}

function hasInjectedDependencies(dependencies = {}) {
  return Object.keys(dependencies).length > 0;
}

function isAnalyticsTestMode(dependencies = {}) {
  return configuredTestMode || dependencies.testMode === true;
}

function normalizeMode(mode) {
  return mode === "revenge" ? "revenge" : "normal";
}

function normalizeSource(source) {
  return source === "revenge" ? "revenge" : "normal";
}

function normalizeDifficulty(difficulty) {
  return difficulty === "hard" ? "hard" : "normal";
}

function normalizeResult(result) {
  return result === "win" ? "win" : "loss";
}

function normalizeAuthProvider(authProvider, signedIn) {
  if (signedIn && authProvider === "google") {
    return "google";
  }
  return "anonymous";
}

function normalizeDurationMs(durationMs) {
  if (durationMs === null || durationMs === undefined || durationMs === "") {
    return null;
  }
  const parsed = Number(durationMs);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return Math.round(parsed);
}

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}
